import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Briefcase, Loader2, Paperclip, X, CheckCircle2, Upload } from 'lucide-react';
import { extraApi } from '@/lib/extraApi';

/**
 * Phase 47.50 — Public "Apply for Job" dialog wired into the homepage
 * Careers section. Captures basic applicant details + a CV file (PDF /
 * DOC / DOCX up to 5 MB, base64 in-lined). On submit the entry lands
 * in `career_applications` and rings the admin notification bell.
 */
const MAX_CV_MB = 5;
const ACCEPTED_MIME = ['application/pdf',
                       'application/msword',
                       'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];

export default function CareerApplyDialog({ open, onOpenChange, career }) {
    const [form, setForm] = useState({
        name: '', mobile: '', email: '', experience_years: '',
        current_location: '', current_ctc: '', expected_ctc: '', notice_period: '',
        cover_letter: '',
    });
    const [cv, setCV] = useState(null);            // { name, mime, size, dataUrl }
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setForm({ name: '', mobile: '', email: '', experience_years: '',
                      current_location: '', current_ctc: '', expected_ctc: '',
                      notice_period: '', cover_letter: '' });
            setCV(null);
            setDone(false);
        }
    }, [open]);

    const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const pickFile = () => fileRef.current?.click();
    const onFilePicked = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        if (!ACCEPTED_MIME.includes(f.type) && !/\.(pdf|doc|docx)$/i.test(f.name)) {
            toast.error('Sirf PDF / DOC / DOCX allowed');
            return;
        }
        if (f.size > MAX_CV_MB * 1024 * 1024) {
            toast.error(`CV file bahut badi hai — max ${MAX_CV_MB} MB`);
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setCV({ name: f.name, mime: f.type, size: f.size, dataUrl: reader.result });
        reader.onerror = () => toast.error('CV read failed — dobara try karein');
        reader.readAsDataURL(f);
    };
    const removeCV = () => setCV(null);

    const submit = async () => {
        // Basic validation
        if (!form.name.trim()) { toast.error('Naam zaroori hai'); return; }
        if (!/^[0-9+\- ]{7,15}$/.test(form.mobile.trim())) { toast.error('Sahi mobile number daalein'); return; }
        if (!career?.title) { toast.error('Position missing'); return; }
        setSubmitting(true);
        try {
            await extraApi.applyCareer({
                name:              form.name.trim(),
                mobile:            form.mobile.trim(),
                email:             form.email.trim() || null,
                position:          career.title,
                experience_years:  Number(form.experience_years || 0),
                cv_data_url:       cv?.dataUrl || null,
                cv_filename:       cv?.name || null,
                cover_letter:      form.cover_letter.trim() || null,
                current_location:  form.current_location.trim() || null,
                current_ctc:       form.current_ctc.trim() || null,
                expected_ctc:      form.expected_ctc.trim() || null,
                notice_period:     form.notice_period.trim() || null,
            });
            setDone(true);
            toast.success('Application submitted / अर्जी सबमिट हो गई');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Submit failed — thodi der baad try karein');
        } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto' data-testid='career-apply-dialog'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2'>
                        <Briefcase className='w-5 h-5 text-primary' />
                        Apply — {career?.title || 'Career Opportunity'}
                    </DialogTitle>
                    <DialogDescription>
                        {career?.location || '—'} · {career?.type || 'Full-time'}
                    </DialogDescription>
                </DialogHeader>

                {done ? (
                    <div className='py-6 text-center space-y-3' data-testid='career-apply-done'>
                        <div className='mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 grid place-items-center'>
                            <CheckCircle2 className='w-6 h-6' />
                        </div>
                        <div className='font-semibold text-lg'>Thank you!</div>
                        <p className='text-sm text-muted-foreground max-w-md mx-auto'>
                            Aapki application receive ho gayi hai. Hamari HR team 3–5 working days me contact karegi
                            agar profile short-list hui.
                        </p>
                        <Button onClick={() => onOpenChange(false)} data-testid='career-apply-close'>Close</Button>
                    </div>
                ) : (
                    <>
                        <div className='grid sm:grid-cols-2 gap-3'>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Full Name *</Label>
                                <Input value={form.name} onChange={e => setField('name', e.target.value)} placeholder='e.g. Amit Verma' data-testid='career-name' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Mobile *</Label>
                                <Input value={form.mobile} onChange={e => setField('mobile', e.target.value)} placeholder='10-digit mobile' data-testid='career-mobile' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Email</Label>
                                <Input type='email' value={form.email} onChange={e => setField('email', e.target.value)} placeholder='name@example.com' data-testid='career-email' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Total Experience (years)</Label>
                                <Input type='number' step='0.5' min='0' value={form.experience_years} onChange={e => setField('experience_years', e.target.value)} placeholder='e.g. 2.5' data-testid='career-experience' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Current Location</Label>
                                <Input value={form.current_location} onChange={e => setField('current_location', e.target.value)} placeholder='e.g. Kanpur' data-testid='career-location' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Notice Period</Label>
                                <Input value={form.notice_period} onChange={e => setField('notice_period', e.target.value)} placeholder='e.g. 30 days / Immediate' data-testid='career-notice' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Current CTC (₹ / year)</Label>
                                <Input value={form.current_ctc} onChange={e => setField('current_ctc', e.target.value)} placeholder='e.g. 3.5 LPA' data-testid='career-current-ctc' />
                            </div>
                            <div className='space-y-1'>
                                <Label className='text-[11px]'>Expected CTC (₹ / year)</Label>
                                <Input value={form.expected_ctc} onChange={e => setField('expected_ctc', e.target.value)} placeholder='e.g. 5 LPA' data-testid='career-expected-ctc' />
                            </div>
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-[11px]'>Cover Letter / Kuch aur bataayein</Label>
                            <Textarea rows={3} value={form.cover_letter} onChange={e => setField('cover_letter', e.target.value)} placeholder='e.g. Solar industry me 3 saal ka experience, UPPCL projects sambhale hain…' data-testid='career-cover' />
                        </div>

                        <div className='space-y-1'>
                            <Label className='text-[11px]'>CV / Resume Upload (PDF/DOC/DOCX · max {MAX_CV_MB} MB)</Label>
                            {cv ? (
                                <div className='flex items-center gap-2 rounded-md border bg-muted/40 p-2 text-xs' data-testid='career-cv-preview'>
                                    <Paperclip className='w-4 h-4 text-primary' />
                                    <div className='flex-1 min-w-0'>
                                        <div className='truncate font-medium'>{cv.name}</div>
                                        <div className='text-[10px] text-muted-foreground'>{(cv.size / 1024).toFixed(0)} KB · {cv.mime || 'document'}</div>
                                    </div>
                                    <Button size='sm' variant='ghost' onClick={removeCV} className='h-7 w-7 p-0' data-testid='career-cv-remove'>
                                        <X className='w-3.5 h-3.5' />
                                    </Button>
                                </div>
                            ) : (
                                <button
                                    type='button'
                                    onClick={pickFile}
                                    className='w-full rounded-md border border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 py-4 text-xs text-primary flex items-center justify-center gap-1.5'
                                    data-testid='career-cv-pick'
                                >
                                    <Upload className='w-4 h-4' /> CV file select karein
                                </button>
                            )}
                            <input
                                ref={fileRef} type='file'
                                accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                                className='hidden'
                                onChange={onFilePicked}
                                data-testid='career-cv-input'
                            />
                        </div>

                        <DialogFooter className='mt-3'>
                            <Button variant='outline' onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
                            <Button onClick={submit} disabled={submitting} data-testid='career-apply-submit'>
                                {submitting ? <Loader2 className='w-4 h-4 animate-spin mr-1' /> : <Briefcase className='w-4 h-4 mr-1' />}
                                Submit Application
                            </Button>
                        </DialogFooter>
                        <div className='text-[10px] text-muted-foreground text-center'>
                            <Badge variant='outline' className='mr-1 h-4'>Privacy</Badge>
                            Aapki details sirf hiring team ke saath share hongi.
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
